import { createAsyncThunk } from '@reduxjs/toolkit';
import { getKeys } from '@/libs/helpers/utils';
import { validateFlightDate } from '@/libs/validation/validateFlightDate';
import { validateFlightTime } from '@/libs/validation/validateFlightTime';
import { validateFlightDeparture } from '@/libs/validation/validateFlightDeparture';
import { FlightField } from './interface';
import { FLIGHTS_STATE_NAMESPACE, flightsActions } from './index';
const flightFields = getKeys(FlightField);
const getFlightLabel = (index) => `${index + 1}-й рейс`;
const getJoinedFlightNumbers = (indexes) => {
    const flightNumbers = indexes.map((index) => index + 1);
    if (flightNumbers.length <= 2) {
        return flightNumbers.join(' и ');
    }
    return `${flightNumbers.slice(0, -1).join(', ')} и ${flightNumbers[flightNumbers.length - 1]}`;
};
const getOverlappingFlightsLabel = (indexes) => indexes.length === 1
    ? `рейсом ${indexes[0] + 1}`
    : `рейсами ${getJoinedFlightNumbers(indexes)}`;
const parseDateTime = (date, time) => {
    const [day, month, year] = date.split('.').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes).getTime();
};
const getFlightDateTimeRange = (flight) => ({
    from: parseDateTime(flight.dateFrom, flight.timeFrom),
    to: parseDateTime(flight.dateTo, flight.timeTo),
});
const getFlightsOverlapError = (flights) => {
    const overlapMessages = [];
    const handledFlightIndexes = new Set();
    for (let currentIndex = 0; currentIndex < flights.length; currentIndex++) {
        if (handledFlightIndexes.has(currentIndex)) {
            continue;
        }
        const currentRange = getFlightDateTimeRange(flights[currentIndex]);
        const overlappingFlightIndexes = [];
        for (let comparedIndex = currentIndex + 1; comparedIndex < flights.length; comparedIndex++) {
            if (handledFlightIndexes.has(comparedIndex)) {
                continue;
            }
            const comparedRange = getFlightDateTimeRange(flights[comparedIndex]);
            const isOverlapping = currentRange.from < comparedRange.to &&
                comparedRange.from < currentRange.to;
            if (isOverlapping) {
                overlappingFlightIndexes.push(comparedIndex);
            }
        }
        if (overlappingFlightIndexes.length) {
            overlappingFlightIndexes.forEach((flightIndex) => handledFlightIndexes.add(flightIndex));
            overlapMessages.push(`${getFlightLabel(currentIndex)} с ${getOverlappingFlightsLabel(overlappingFlightIndexes)}`);
        }
    }
    return overlapMessages.length
        ? `Некоторые рейсы пересекаются по времени: ${overlapMessages.join('; ')}. Проверьте даты и время вылета/прилёта.`
        : null;
};
const getFlightsOrderError = (flights) => {
    for (let index = 1; index < flights.length; index++) {
        const previousFlightDeparture = parseDateTime(flights[index - 1].dateFrom, flights[index - 1].timeFrom);
        const currentFlightDeparture = parseDateTime(flights[index].dateFrom, flights[index].timeFrom);
        if (currentFlightDeparture < previousFlightDeparture) {
            return 'Рейсы должны идти по времени вылета от раннего к позднему. Измените порядок вручную или нажмите «Расставить автоматически» и проверьте результат.';
        }
    }
    return null;
};
export const validateFlights = createAsyncThunk('flights/validate', async (_, thunkApi) => {
    const { [FLIGHTS_STATE_NAMESPACE]: { flights }, } = thunkApi.getState();
    flights.forEach((flight) => {
        const { error: dateFromError } = validateFlightDate(flight.dateFrom);
        const { error: dateToError } = validateFlightDate(flight.dateTo, flight.dateFrom);
        const { error: timeFromError } = validateFlightTime(flight.timeFrom);
        const { error: departureError } = validateFlightDeparture({
            dateFrom: flight.dateFrom,
            timeFrom: flight.timeFrom,
        });
        const { error: timeToError } = validateFlightTime(flight.timeTo, {
            dateFrom: flight.dateFrom,
            dateTo: flight.dateTo,
            timeFrom: flight.timeFrom,
        });
        const fieldErrors = [
            [FlightField.dateFrom, dateFromError],
            [FlightField.dateTo, dateToError],
            [FlightField.timeFrom, timeFromError || departureError],
            [FlightField.timeTo, timeToError],
        ];
        fieldErrors.forEach(([field, error]) => {
            thunkApi.dispatch(flightsActions._setFlightFieldError({
                _uuid: flight._uuid,
                field,
                error,
            }));
        });
    });
    const { [FLIGHTS_STATE_NAMESPACE]: { flights: validatedFlights }, } = thunkApi.getState();
    const areFlightsValid = validatedFlights.every((flight) => flightFields.every((field) => {
        const hasValue = flight[field]?.trim();
        const hasNoError = !flight.fieldsErrors[field];
        return hasValue && hasNoError;
    }));
    if (!areFlightsValid) {
        thunkApi.dispatch(flightsActions._setStepError({ error: null }));
        thunkApi.dispatch(flightsActions._setFlightsValidated(false));
        return;
    }
    const overlapError = getFlightsOverlapError(validatedFlights);
    if (overlapError) {
        thunkApi.dispatch(flightsActions._setStepError({
            error: overlapError,
            type: 'overlap',
        }));
        thunkApi.dispatch(flightsActions._setFlightsValidated(false));
        return;
    }
    const orderError = getFlightsOrderError(validatedFlights);
    if (orderError) {
        thunkApi.dispatch(flightsActions._setStepError({
            error: orderError,
            type: 'order',
        }));
        thunkApi.dispatch(flightsActions._setFlightsValidated(false));
        return;
    }
    thunkApi.dispatch(flightsActions._setStepError({ error: null }));
    thunkApi.dispatch(flightsActions._setFlightsValidated(true));
});
